import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { TileMark, Wordmark } from "@/components/primitives";
import { cn } from "@/lib/utils";

/** The marquee word. Spelled out tile by tile, the way a round resolves. */
const DEMO: { letter: string; tone: string }[] = [
  { letter: "K", tone: "tile-hit" },
  { letter: "E", tone: "tile-hit" },
  { letter: "R", tone: "tile-near" },
  { letter: "N", tone: "tile-miss" },
  { letter: "I", tone: "tile-hit" },
  { letter: "N", tone: "tile-near" },
  { letter: "G", tone: "tile-miss" },
];

const RULES = [
  {
    title: "One word, everyone at once",
    body: "Every player in the room races the same design term. Four to eight letters, one more guess than it has letters.",
  },
  {
    title: "Fewest guesses wins the round",
    body: "Points come from how few guesses it took. The clock only splits two people who took the same number.",
  },
  {
    title: "Nobody sees your board",
    body: "The room learns that you solved it, not how. Guess counts land at round end, all at once.",
  },
];

export default function Landing() {
  return (
    <main className="min-h-[100dvh]">
      <header className="flex h-14 items-center justify-between gap-2 border-b border-line px-4">
        <TileMark size={20} />
        <Wordmark className="shrink-0 text-[17px] sm:text-[24px]" />
        <Link
          to="/join"
          className="text-[12px] font-bold uppercase tracking-[0.1em] text-ink-dim transition-colors hover:text-ink"
        >
          Play
        </Link>
      </header>

      <section className="mx-auto flex w-full max-w-[560px] flex-col items-center px-5 py-14 text-center sm:py-20">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
          className="flex w-full justify-center gap-1.5"
        >
          {DEMO.map((cell, i) => (
            <motion.span
              key={i}
              initial={{ rotateX: 90, opacity: 0 }}
              animate={{ rotateX: 0, opacity: 1 }}
              transition={{ delay: 0.12 * i, duration: 0.35, ease: "easeOut" }}
              className={cn("tile min-w-0", cell.tone, "text-[clamp(16px,5.2vw,26px)]")}
              style={{ maxWidth: 56 }}
            >
              {cell.letter}
            </motion.span>
          ))}
        </motion.div>

        <h1 className="wordmark mt-10 text-[34px] leading-[1.05] sm:text-[44px]">
          Guess the design word
        </h1>
        <p className="mt-5 max-w-[42ch] text-[16px] leading-relaxed text-ink-dim">
          Wordle for people who kern for a living. Typography, video editing, motion, Canva, digital
          art. Play it in a room with everyone else, over rounds, for points.
        </p>

        <div className="mt-9 flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
          <Link to="/join" className="btn btn-primary w-full sm:w-auto">
            Join a room
          </Link>
          <Link to="/join?host=1" className="btn btn-ghost w-full sm:w-auto">
            Host a room
          </Link>
        </div>

        <div className="mt-16 w-full border-t border-line pt-10 text-left">
          <ul className="flex flex-col gap-8">
            {RULES.map((rule) => (
              <li key={rule.title}>
                <h2 className="text-[15px] font-bold">{rule.title}</h2>
                <p className="mt-2 text-[14px] leading-relaxed text-ink-dim">{rule.body}</p>
              </li>
            ))}
          </ul>
        </div>

        <p className="mt-16 text-[12px] uppercase tracking-[0.14em] text-ink-faint">
          Rooms live only while the server does
        </p>
      </section>
    </main>
  );
}
